import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Wallet, Coins, Gift, ShoppingBag, Clock, ArrowUp, ArrowDown,
  Loader2, QrCode, Sparkles, TrendingUp, Hotel, Lock,
  CreditCard, Package, Palette, ChevronRight, Star, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


function getGuestId() {
  let id = localStorage.getItem('bitsy_guest_id');
  if (!id) { id = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem('bitsy_guest_id', id); }
  return id;
}

const TIER_ICONS = { quick_scan: QrCode, read_story: Star, deep_engage: Sparkles };
const TIER_COLORS = { quick_scan: 'text-gray-400', read_story: 'text-blue-400', deep_engage: 'text-[#F7931A]' };
const SPEND_ICONS = { room_upgrade: Hotel, booking_discount: CreditCard, product_discount: Package, marketplace: Palette, staking: Lock };
const SPEND_COLORS = { room_upgrade: 'from-blue-600 to-blue-800', booking_discount: 'from-emerald-600 to-emerald-800', product_discount: 'from-purple-600 to-purple-800', marketplace: 'from-pink-600 to-pink-800', staking: 'from-amber-600 to-amber-800' };

export default function GuestEarnPage() {
  const [searchParams] = useSearchParams();
  const guestId = getGuestId();
  const [wallet, setWallet] = useState(null);
  const [scans, setScans] = useState([]);
  const [options, setOptions] = useState([]);
  const [spends, setSpends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('earn');
  const [claiming, setClaiming] = useState(false);

  // Auto-claim if redirected from product page with params
  const autoClaim = searchParams.get('claim');
  const productId = searchParams.get('product');
  const vendorId = searchParams.get('vendor');
  const hotelId = searchParams.get('hotel');
  const roomId = searchParams.get('room');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [w, h, o, s] = await Promise.allSettled([
        fetch(`${API}/api/blockchain/v2/learn-to-earn/wallet/${guestId}`).then(r => r.json()),
        fetch(`${API}/api/blockchain/v2/learn-to-earn/history/${guestId}`).then(r => r.json()),
        fetch(`${API}/api/blockchain/v2/learn-to-earn/spend/options/${guestId}`).then(r => r.json()),
        fetch(`${API}/api/blockchain/v2/learn-to-earn/spend/history/${guestId}`).then(r => r.json()),
      ]);
      if (w.status === 'fulfilled') setWallet(w.value);
      if (h.status === 'fulfilled') setScans(h.value?.scans || []);
      if (o.status === 'fulfilled') setOptions(o.value?.options || []);
      if (s.status === 'fulfilled') setSpends(s.value?.spends || []);
    } catch {}
    setLoading(false);
  }, [guestId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-claim on redirect
  useEffect(() => {
    if (autoClaim && productId && vendorId && hotelId && !claiming) {
      setClaiming(true);
      fetch(`${API}/api/blockchain/v2/learn-to-earn/scan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId, product_id: productId, vendor_id: vendorId, hotel_id: hotelId, room_id: roomId || 'unknown', engagement_type: 'quick_scan' }),
      })
        .then(r => r.ok ? r.json() : r.json().then(e => { throw e; }))
        .then(d => { toast.success(`+${d.reward_amount} HERMES collected!`); loadData(); })
        .catch(e => { toast.info(e?.detail || 'Already claimed this reward'); })
        .finally(() => setClaiming(false));
    }
  }, [autoClaim, productId, vendorId, hotelId, roomId, guestId, claiming, loadData]);

  const handleSpend = async (type, amount) => {
    try {
      const res = await fetch(`${API}/api/blockchain/v2/learn-to-earn/spend`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId, amount, spend_type: type, description: `Redeemed ${type.replace(/_/g, ' ')}` }),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail); return; }
      const d = await res.json();
      toast.success(`Redeemed! New balance: ${d.new_balance} HERMES`);
      loadData();
    } catch { toast.error('Redemption failed'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F7931A]" />
      </div>
    );
  }

  const balance = wallet?.balance || 0;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="guest-earn-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0e1a]/90 backdrop-blur-xl">
        <div className="max-w-lg mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
              <Coins className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-bold heading-font tracking-tight">Collect & Spend</span>
          </div>
          <Badge variant="outline" className="text-[9px] border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5">
            HERMES Protocol
          </Badge>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Wallet Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1f2e] to-[#0f1420] border border-white/5 p-6" data-testid="wallet-card">
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#F7931A]/5 rounded-full -mr-10 -mt-10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-[#F7931A]" />
              <span className="text-[10px] text-gray-500 uppercase tracking-widest">Your Balance</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl sm:text-5xl font-bold heading-font text-white" data-testid="balance-amount">{balance.toLocaleString()}</span>
              <span className="text-sm text-[#F7931A] font-medium">HERMES</span>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-1.5">
                <ArrowDown className="w-3 h-3 text-emerald-400" />
                <span className="text-xs text-gray-400">Collected: <span className="text-emerald-400 font-medium">{(wallet?.total_earned || 0).toLocaleString()}</span></span>
              </div>
              <div className="flex items-center gap-1.5">
                <ArrowUp className="w-3 h-3 text-blue-400" />
                <span className="text-xs text-gray-400">Spent: <span className="text-blue-400 font-medium">{(wallet?.total_spent || 0).toLocaleString()}</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-white/[0.03] rounded-xl border border-white/5 p-1" data-testid="earn-spend-tabs">
          {[
            { id: 'earn', label: 'Collection History', icon: TrendingUp },
            { id: 'rewards', label: 'Rewards', icon: Gift },
            { id: 'spent', label: 'Spent', icon: ShoppingBag },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium transition-all ${
                tab === t.id
                  ? 'bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/20'
                  : 'text-gray-500 hover:text-gray-300 border border-transparent'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Earn History */}
        {tab === 'earn' && (
          <div className="space-y-2" data-testid="earn-history">
            {scans.length === 0 ? (
              <div className="text-center py-12">
                <QrCode className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No scans yet</p>
                <p className="text-xs text-gray-600 mt-1">Scan any product QR code in your room to start collecting</p>
              </div>
            ) : (
              scans.map((scan, i) => {
                const Icon = TIER_ICONS[scan.engagement_type] || QrCode;
                const color = TIER_COLORS[scan.engagement_type] || 'text-gray-400';
                return (
                  <div key={i} className="flex items-center justify-between py-3 px-4 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`scan-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/5 flex items-center justify-center">
                        <Icon className={`w-4 h-4 ${color}`} />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-200">{scan.product_id?.replace(/_/g, ' ')}</p>
                        <p className="text-[10px] text-gray-600">{scan.reward_tier} | {scan.hotel_id?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-emerald-400">+{scan.reward_amount}</p>
                      <p className="text-[9px] text-gray-600">{new Date(scan.timestamp).toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Rewards Shop */}
        {tab === 'rewards' && (
          <div className="space-y-3" data-testid="rewards-shop">
            {options.map((opt, i) => {
              const Icon = SPEND_ICONS[opt.type] || Gift;
              const gradient = SPEND_COLORS[opt.type] || 'from-gray-600 to-gray-800';
              const minCost = opt.cost_range?.min || 0;
              return (
                <div
                  key={i}
                  className={`relative overflow-hidden rounded-xl border ${opt.available ? 'border-white/10 hover:border-white/20' : 'border-white/5 opacity-50'} transition-all`}
                  data-testid={`reward-card-${opt.type}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-5`} />
                  <div className="relative flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{opt.label}</p>
                        <p className="text-[10px] text-gray-500 max-w-[200px]">{opt.description}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      {opt.available ? (
                        <Button
                          size="sm"
                          className="text-xs h-8 bg-[#F7931A] hover:bg-[#F7931A]/80 text-white"
                          onClick={() => handleSpend(opt.type, minCost)}
                          data-testid={`redeem-${opt.type}`}
                        >
                          {minCost} <Coins className="w-3 h-3 ml-1" />
                        </Button>
                      ) : (
                        <div>
                          <p className="text-[10px] text-gray-600">Need {minCost}+</p>
                          <p className="text-[9px] text-gray-700">HERMES</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Spend History */}
        {tab === 'spent' && (
          <div className="space-y-2" data-testid="spend-history">
            {spends.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No redemptions yet</p>
                <p className="text-xs text-gray-600 mt-1">Earn HERMES by scanning products, then redeem for rewards</p>
              </div>
            ) : (
              spends.map((spend, i) => {
                const Icon = SPEND_ICONS[spend.spend_type] || Gift;
                return (
                  <div key={i} className="flex items-center justify-between py-3 px-4 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`spend-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/5 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-200">{spend.spend_type?.replace(/_/g, ' ')}</p>
                        <p className="text-[10px] text-gray-600">{spend.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-blue-400">-{spend.amount}</p>
                      <p className="text-[9px] text-gray-600">{new Date(spend.timestamp).toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* How It Works */}
        <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4" data-testid="how-it-works">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-3">How It Works</p>
          <div className="space-y-3">
            {[
              { step: '1', text: 'Scan any product QR code in your room', reward: '+2 HERMES' },
              { step: '2', text: 'Read the product story (30s+)', reward: '+5 HERMES' },
              { step: '3', text: 'Explore 3D showroom or watch video', reward: '+10 HERMES' },
              { step: '4', text: 'Spend HERMES on upgrades & discounts', reward: '' },
            ].map(s => (
              <div key={s.step} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#F7931A]/10 border border-[#F7931A]/20 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold text-[#F7931A]">{s.step}</span>
                </div>
                <p className="text-xs text-gray-400 flex-1">{s.text}</p>
                {s.reward && <span className="text-[10px] font-mono text-emerald-400 shrink-0">{s.reward}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Powered by */}
        <div className="flex items-center justify-center gap-2 py-4">
          <Shield className="w-3 h-3 text-gray-700" />
          <span className="text-[9px] text-gray-700">Powered by HERMES Protocol | Max 50 HERMES/day</span>
        </div>
      </div>
    </div>
  );
}
